<template>
    <component
            v-bind:is="component"
            :target="target"
            class="likefolder-action-button"
            :class="{
                'no-text-mobile':noTextOnMobile,
                'disabled':disabled,
            }"
            v-tooltip="tooltipText"
            :style="folderButtonStyle"
            @focus="onFocus"
            @blur="onBlur"
    >
        <div :class="[buttonColor, customIcon, buttonFocus, {'likefolder-action-button-icon_transparent': outline || noBackground}]"
             :style="customIconSizeStyle"
             class="likefolder-action-button-icon">
            <slot name="icon">
                <i :class="[iconClass, iconPrefix]" v-show="iconClass" aria-hidden="true"
                   :style="iconStyle">
                </i>
            </slot>
        </div>
        <div class="likefolder-action-button-name"
             :class="{
            'no-text-mobile':noTextOnMobile,
            'disabled':disabled
        }"
        >
            <slot name="text">{{ text }}</slot>
        </div>
    </component>
</template>
<script>
    import FolderButtonSpan from './FolderButtonSpan';
    import FolderButtonLink from './FolderButtonLink';
    import FolderButtonRouterLink from './FolderButtonRouterLink';
    import RouterCheckAvailabilityMixin from '../RouterCheckAvailabilityMixin';

    const DEFAULT_MAX_WIDTH = 88;

    export default {
        name: 'VueFolderButton',
        mixins: [RouterCheckAvailabilityMixin],
        // no `emits` here on purpose: the `onClick` this button is bound with is emitted by
        // FolderButtonSpan below, and declaring it here would consume the listener as this
        // component's own emit instead of letting it fall through to the child that fires it
        data() {
            return {
                hasFocus: false,
            };
        },
        props: {
            noTextOnMobile: {
                type: Boolean,
                default: false
            },
            text: {
                type: [String, Number],
                required: false
            },
            iconClass: {
                type: String,
                required: false
            },
            customIcon: {
                type: String,
                required: false
            },
            color: {
                type: String,
                required: false,
                default: 'tag-gray'
            },
            outline: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            innerColor: {
                type: String,
                default: 'var(--color-on-fill)'
            },
            active: {
                type: Boolean,
                required: false,
                default: false
            },
            activeColor: {
                type: String,
                default: 'black'
            },
            helpText: {
                type: String,
                required: false
            },
            helpTextInfoIcon: {
                type: Boolean,
                default: false
            },
            top: {
                type: Number,
                default: -2
            },
            left: {
                type: Number,
                required: false
            },
            maxWidth: {
                required: false,
                type: Number
            },
            customIconSize: {
                type: Number,
                default: 54
            },
            target: {
                type: [String, Object],
                required: false,
            },
            isRouterLink: {
                type: Boolean,
                default: false,
            },
            noBackground: {
                type: Boolean,
                default: false,
            },
            iconPrefix: {
                type: String,
                default: 'fa',
            },
        },
        computed: {
            component() {
                if (this.target) {
                    if (this.isRouterLink) {
                        return FolderButtonRouterLink;
                    } else {
                        if (this.getRouterAvailability(this.target)) {
                            return FolderButtonRouterLink;
                        } else {
                            return FolderButtonLink;
                        }
                    }
                }
                return FolderButtonSpan;
            },
            customIconSizeStyle() {
                const style = { 'background-size': `${this.customIconSize}%` };
                if (this.left !== undefined) {
                    style['background-position-x'] = `${this.left}px`;
                }
                if (this.outline) {
                    const resolvedColor = this.color || 'var(--color-text-secondary)';
                    style['background-color'] = 'transparent';
                    style['border'] = `2px solid ${resolvedColor}`;
                    style['box-sizing'] = 'border-box';
                }
                return style;
            },
            tooltipText() {
                if (this.helpText) {
                    if (this.helpTextInfoIcon) {
                        return `<i class='fa fa-info-circle'></i> ` + this.helpText;
                    } else {
                        return this.helpText;
                    }
                }
            },
            buttonFocus() {
                if (this.hasFocus) {
                    return 'has-focus';
                }
            },
            buttonColor() {
                if (this.noBackground) {
                    return;
                }
                if (this.disabled) {
                    return 'lightgray';
                }
                if (this.active) {
                    return this.activeColor;
                }
                return this.color;
            },
            iconStyle() {
                const resolvedColor = this.color || this.innerColor;
                const style = {
                    color: (this.noBackground || this.outline) ? 'var(--color-text-secondary)' : this.innerColor,
                };
                if (this.outline) {
                    style.color = resolvedColor;
                }
                return style;
            },
            folderButtonStyle() {
                const style = {};
                if (this.maxWidth) {
                    // given in px at the default text size, and grows with the circle
                    style['max-width'] = `${this.maxWidth / 16}rem`;
                }
                if (this.left !== undefined) {
                    style['left'] = `${this.left}px`;
                }
                return style;
            }
        },
        methods: {
            onFocus() {
                this.hasFocus = true;
            },
            onBlur() {
                this.hasFocus = false;
            }
        }
    };
</script>

<style scoped lang="scss">
  @use "../../../angular/app/styles/mixins" as *;

  .likefolder-action-button {
    max-width: 5.5rem;
    vertical-align: top;
    display: inline-block;
    cursor: pointer;

    &.no-text-mobile {
      @media (max-width: 500px) {
        min-width: unset !important;
      }
    }

    @media (min-width: 1200px) {
      margin: 0 3px;
    }
    @media (min-width: $screen-sm-min) and (max-width: 1199px) {
      margin: 0 1px;
    }
    @media (max-width: $screen-xs-max) {
      margin: 0 1px;
    }

    &.disabled {
      cursor: not-allowed;
    }
  }

  .likefolder-action-button-icon {
    @include mm-icons;

    // no fill behind it, only the black page
    &_transparent {
      :root[data-theme="high-contrast"] & {
        @include mm-icons(panel);
      }
    }

    margin: auto;
    height: 2.5625rem;
    width: 2.5625rem;
    font-size: 1.3125rem;
    line-height: 2.5625rem;
    text-align: center;
    color: var(--color-on-fill);
    border-radius: 50%;

    background-size: 54%;
    background-repeat: no-repeat;
    background-position: center;

    transition: background-color ease 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      position: relative;
      top: 0;
    }

    &:hover {
      box-shadow: inset 0 0 100px 100px color-mix(in srgb, var(--color-surface) 20%, transparent);
    }

    &.blue {
      background-color: $blue;
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-accent-blue) 50%, transparent);
      }
    }

    &.dark-blue {
      background-color: $dark-blue;
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-accent-navy) 50%, transparent);
      }
    }

    &.black {
      background-color: var(--color-surface-inverse);
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-text-strong) 50%, transparent);
      }
    }

    &.red {
      background-color: $red;
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-accent-red) 50%, transparent);
      }
    }

    &.green {
      background-color: $green;
    }

    &.grass-green {
      background-color: $grass-green;
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-accent-green) 50%, transparent);
      }
    }

    &.yellow {
      background-color: $yellow;
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-accent-yellow) 50%, transparent);
      }
    }

    &.brown {
      background-color: $brown;
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-accent-brown) 50%, transparent);
      }
    }

    &.lightgray {
      background-color: var(--color-fill-faint);
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-border-strong) 50%, transparent);
      }
    }

    &.gray {
      background-color: var(--color-fill-muted);
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-tag-bg) 50%, transparent);
      }
    }

    &.tag-gray {
      background-color: var(--color-tag-bg);
      &.has-focus {
        box-shadow: 0 0 5px 3px color-mix(in srgb, var(--color-tag-bg) 50%, transparent);
      }
    }

    &.color-folder {
      background-color: $color-folder;
    }

    &.color-header {
      background-color: var(--color-surface-sunken);
    }

    &.color-goal {
      background-color: $color-goal;
    }

    &.color-story {
      background-color: $color-story;
    }

    &.color-diary {
      background-color: $color-diary;
    }
  }

  .likefolder-action-button-name {
    text-align: center;
    font-weight: bolder;
    vertical-align: top;
    font-size: 0.75rem;
    color: var(--color-text);

    &.no-text-mobile {
      @media (max-width: 500px) {
        display: none;
      }
    }

    &.disabled {
      color: var(--color-text-faint);
    }
  }
</style>